import type { RuleId, VerifyStrategy } from '@smart-link/shared';

/** 验证策略的下拉选项(链接页 + 平台规则页共用),避免两处文案漂移。 */
export const VERIFY_STRATEGY_OPTIONS: readonly { value: VerifyStrategy; label: string }[] = [
  { value: 'challenge', label: '点击验证 (预热·点击秒跳)' },
  { value: 'auto', label: '免点击·自动跳转' },
];

/** 短标签,用于"继承平台 (…)"这类紧凑展示。 */
export const VERIFY_STRATEGY_SHORT: Record<VerifyStrategy, string> = {
  challenge: '点击秒跳',
  auto: '自动跳转',
};

export const RULE_LABEL: Record<RuleId, string> = {
  datacenterAsn: '数据中心 ASN',
  missingTtclid: '缺少广告点击 ID 且非广告平台来源',
  webdriverFlag: 'WebDriver 标记 (Selenium/Puppeteer)',
  headlessUa: 'Headless UA',
  noInteraction: '无任何用户交互',
  mobileMismatch: '移动 UA 但桌面屏幕',
  notInGeoAllow: '国家不在白名单',
  isDesktop: '屏蔽 PC / 桌面设备',
  challengeFailed: '未通过主动挑战',
  noMotionSensors: '移动 UA 无运动传感器',
  noMediaDevices: '无音视频设备',
  platformUaMismatch: 'Platform/UA 操作系统不一致',
  noStorage: '无 localStorage / sessionStorage',
  lowPerfJitter: '时钟抖动过小 (自动化嫌疑)',
  desktopGpuOnMobile: '【移动】桌面 GPU 出现在移动设备',
  noTouchOnMobile: '【移动】无触摸能力',
  lowDprOnMobile: '【移动】像素密度过低',
  nonCoarsePointer: '【移动】指针非 coarse (像鼠标)',
  noVibrateOnMobile: '【移动】无振动 API',
  noOrientationOnMobile: '【移动】无屏幕方向 API',
  googlePayNotReady: '【安卓】Google Pay 不可用 (isReadyToPay=false/失败)',

  notInAppWebview: '【移动】非广告平台 in-app 浏览器',
  // 高级反检测
  hasDriverMarker: '检测到自动化工具痕迹 (Selenium/CDP)',
  nativeFnTampered: '原生函数被改写 (anti-detect 工具)',
  missingClientHints: '缺少 Client Hints 头 (sec-ch-ua-*)',
  clientHintsMismatch: 'Client Hints 与 UA 不一致',
  syntheticTouch: '合成触摸事件 (force/radius 全 0)',
  zeroSpeechVoices: 'speechSynthesis 返回 0 个语音',
  cssFeatureMismatch: 'CSS 特性支持与 UA 版本不符',
  lowMouseEntropy: '鼠标轨迹熵过低 (直线移动)',
  // v2: Probe 交叉验证
  timezoneMismatch: '时区与 IP 国家不匹配',
  languageMismatch: '语言与 IP 国家不匹配',
  probeVerifyIpChange: 'Probe/Verify IP 不一致 (代理切换)',
  // v2: 新客户端信号
  rafTimingFlat: 'rAF 帧间隔过于均匀 (自动化嫌疑)',
  canvasNoise: 'Canvas 渲染不稳定 (反指纹插件)',
  lowScrollEntropy: '滚动行为熵过低 (脚本)',
  lowClickEntropy: '点击位置熵过低 (脚本)',
  botdBot: 'BotD 库判定为爬虫',
  proxyIp: '代理 / VPN / 匿名 ASN',
  tlsHttpMismatch: 'TLS / HTTP 版本过旧 (自动化嫌疑)',
  crossLinkDevice: '同一设备跨多个链接访问 (审核/爬虫嫌疑)',
  // 观察规则 (权重 1，仅记录命中、不影响放行)
  notificationPermMismatch: '【观察】通知权限状态不一致 (headless 嫌疑)',
  noPlatformAuthenticator: '【观察】移动端无平台认证器 (无指纹/FaceID)',
  signalCollectTimeout: '【观察】指纹收集超时降级 (恶劣 webview,部分信号放行)',
  // v5: edge token 交叉校验(HTML 请求时刻 vs verify 时刻,无状态)
  edgeTokenMissing: '未携带有效 edge token (缓存页/绕过落地页嫌疑)',
  edgeIpDrift: 'HTML 请求与验证间网络切换 (代理轮换嫌疑)',
  edgeVerifyTooFast: 'HTML 下发到验证间隔过短 (自动化嫌疑)',
  staticDevice: '安卓设备全程静止且零交互 (设备农场嫌疑)',
  sameLinkReentry: '同一设备反复到访同一链接 (刷量/踩点嫌疑)',
};

// 非规则引擎产生的 reason(生命周期 / 历史遗留 key),仍会出现在 audits.reasons 里
const EXTRA_LABEL: Record<string, string> = {
  pageView: '仅浏览未提交验证',
  leftBeforeVerify: '验证前离开',
  edgeRequest: '边缘请求 (HTML 已抓取,JS 未执行)',
  redirectAttempt: '尝试跳转',
  redirectAppSwitched: '已切换到 App',
  // 历史别名:notInAppWebview 的旧 key
  notTikTokWebview: '【移动】非广告平台 in-app 浏览器',
};

export function ruleLabel(id: string): string {
  return RULE_LABEL[id as RuleId] ?? EXTRA_LABEL[id] ?? id;
}
